<template>
	<div class="w-full p-6 print:py-2">
		<h2
			class="form-title pb-6 text-2xl font-bold print:mb-3 print:border-b-2 print:border-slate-900 print:pt-2 print:pb-3 print:text-sm/0"
		>
			{{ t('plan.sectionTitle.spouseAndMarriage') }}
		</h2>
		<section>
			<CopyButtonList
				:active-index="activePlanIndex"
				@click="handleCopySpouseInfo"
				class="print:hidden"
			/>

			<FormKit
				id="spouseInfo"
				name="spouseInfo"
				type="group"
				class="print:*:break-inside-avoid"
			>
				<div
					class="section-grid-row grid grid-cols-4 gap-2 *:w-full *:max-w-none"
				>
					<FormKit
						type="textarea"
						auto-height
						name="firstName"
						:label="t('plan.spouseInfo.firstName')"
					/>

					<FormKit
						type="textarea"
						auto-height
						name="middleName"
						:label="t('plan.spouseInfo.middleName')"
					/>

					<FormKit
						type="textarea"
						auto-height
						name="lastName"
						:label="t('plan.spouseInfo.lastName')"
					/>

					<FormKit
						type="textarea"
						auto-height
						name="maidenName"
						:label="t('plan.spouseInfo.maidenName')"
					/>
				</div>

				<div
					class="section-grid-row grid grid-cols-2 gap-2 *:w-full *:max-w-none"
				>
					<FormKit
						type="date"
						name="marriageDate"
						:label="t('plan.spouseInfo.marriageDate')"
						:validation="[['date_between', pastDateMin, currentDate]]"
						validation-visibility="live"
						:help="
							t('plan.helpText.recorded', {
								helpTextLabel: t('plan.spouseInfo.marriageDate'),
							})
						"
					/>

					<FormKit
						type="textarea"
						auto-height
						name="marriageCity"
						:label="t('plan.spouseInfo.marriageCity')"
					/>
				</div>

				<div
					class="section-grid-row grid grid-cols-2 gap-2 *:w-full *:max-w-none"
				>
					<FormKit
						type="autocomplete"
						:label="t('plan.spouseInfo.marriageState')"
						name="marriageState"
						:placeholder="
							t('plan.placeholders.select', {
								placeholderLabel: t('plan.spouseInfo.marriageState'),
							})
						"
						:options="stateOptions"
						clear-search-on-open
					/>
					<FormKit
						type="textarea"
						auto-height
						name="marriageCounty"
						:label="t('plan.spouseInfo.marriageCounty')"
					/>
				</div>
			</FormKit>
		</section>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import CopyButtonList from '@/components/presentation/CopyButtonList.vue'
import { stateOptions } from '@/data/stateOptions'
import { usePresentationLocale } from '@/i18n'
import { useSessionStore } from '@/stores/session'
import { FormKit } from '@formkit/vue'

const { t } = usePresentationLocale()

const pastDateMin = '1900-01-01' // Minimum date value
const currentDate = new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format

const props = defineProps<{
	activePlanIndex: number
	plan: Plan
}>()

/**
 * Copies spouse information from the active session to the specified plan index.
 * @param {number} buttonIndex - The index of the button that triggered the copy action.
 * @return {void} This function does not return a value.
 */
function handleCopySpouseInfo(buttonIndex: number) {
	const sessionStore = useSessionStore()
	const sourcePlan = sessionStore.activePlans?.[buttonIndex]
	const targetPlan = sessionStore.activePlans?.[props.activePlanIndex]

	if (sourcePlan && targetPlan) {
		// Set the spouse of the target to the source
		targetPlan.spouseInfo = {
			firstName: sourcePlan.firstName,
			middleName: sourcePlan.middleName,
			lastName: sourcePlan.lastName,
			maidenName: sourcePlan.maidenName,
			marriageDate: sourcePlan.spouseInfo.marriageDate,
			marriageCity: sourcePlan.spouseInfo.marriageCity,
			marriageState: sourcePlan.spouseInfo.marriageState,
			marriageCounty: sourcePlan.spouseInfo.marriageCounty,
		}
	}

	// Rely on the session view/plan store to save the updated plan
}
</script>
